import { LocationQueryValue } from "vue-router";
import { WSClient } from "./WSClient";
import { TempNode } from "@/types/utils";
import { Cat } from "@/types/serie";
import DOMPurify from "dompurify";
import { useAppContextStore } from "@/store/app_context";

type NullableString = string | undefined | null;

/**
 * Checks if a given series ID corresponds to a demo series.
 * @param serie_id - The ID of the series to check.
 * @param client - An optional WSClient instance for querying.
 * @returns A promise that resolves to true if it's a demo series, false otherwise.
 */
export async function isDemoSerie(serie_id: string, client?: WSClient): Promise<boolean> {
    if (serie_id === "demo") return true;
    const app_context = useAppContextStore();
    return app_context.demo_series_id() + "" == serie_id;
}

/**
 * Trims a string or number to a maximum length and adds ellipsis if necessary.
 * @param input - The value to trim.
 * @param max - The maximum length of the string.
 * @returns The trimmed string or an empty string if input is null or undefined.
 */
export function elipsis(input: unknown, max: number): string {
    if (input == null || input == undefined) return "";
    const normalized = typeof input === "string" ? input : String(input);
    if (normalized.length < max) return normalized;
    return normalized.substring(0, max - 3) + "...";
}

/**
 * Trims a string or number to a maximum length and adds ellipsis if necessary in the middle.
 * @param input - The string or number to trim.
 * @param start - The maximum length of the string before the ellipsis.
 * @param end - The maximum length of the string after the ellipsis.
 * @returns The trimmed string or an empty string if input is null or undefined.
 */
export function elipsis_middle(text: string | number, start: number, end: number): string {
    if (typeof text === "number") text = String(text)
    // Leading spaces are used to order list values: they must not eat the visible start.
    text = text.trim();
    if (text.length <= start + end) {
        return text;
    }
    return text.slice(0, start) + "…" + text.slice(text.length - end);
}

/**
 * Opens a help article in the Freshworks widget.
 * @param articleId - The ID or number of the article to open.
 */
export function openHelp(articleId: string | number) {
    (window as any)?.FreshworksWidget("open", "article", { id: articleId });
}

/**
 * Retrieves the first parameter from a given input.
 * @param param - The parameter to retrieve, which can be a string, array, or LocationQueryValue.
 * @returns The first parameter or null if none exists.
 */
export function getFirstParam(param: string | string[] | LocationQueryValue | LocationQueryValue[]): string | null {
    if (!param) return null;
    return Array.isArray(param) ? param[0] : param;
}

/**
 * Parses a string or number to an integer, returning a default value if invalid.
 * @param intStr - The string or number to parse.
 * @param default_num - The default number to return if parsing fails.
 * @returns The parsed integer or the default number.
 */
export function parseIntId(intStr: number | NullableString, default_num: number): number {
    if (intStr === undefined || intStr === null || intStr === "") return default_num;
    if (typeof intStr === "number") return intStr;
    return parseInt(intStr);
}

/**
 * True when the questionnaire editor route refers to a saved questionnaire (numeric id), not the create wizard.
 */
export function isPersistedQuestionnaireId(id: number | NullableString | string[]): boolean {
    const raw = Array.isArray(id) ? id[0] : id;
    const s = String(raw ?? "").trim();
    return s !== "" && s !== "new" && !Number.isNaN(Number(s));
}

/**
 * Capitalizes the first letter of a string.
 * @param str - The string to capitalize.
 * @returns The capitalized string.
 */
export function capitalize(str: NullableString): string {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Returns the plural form of a string based on a count.
 * @param str - The string to pluralize.
 * @param nb - The count to determine if pluralization is needed.
 * @returns The original string or the pluralized version.
 */
export function plurals(str: string, nb: number | undefined | null): string {
    if (nb === undefined || nb === null || nb === 1) return str;
    return str + "s";
}

/**
 * Joins an array of strings with commas and an "and" before the last item.
 * @param arr - The array of strings to join.
 * @returns The joined string.
 */
export function joinAnd(arr: string[]): string {
    let resp = arr.join(",");
    if (arr.length > 1) {
        resp = arr.slice(0, -1).join(", ");
        resp = resp + " and " + arr.slice(-1);
    }
    return resp;
}

/**
 * Finds the index of an element in an array based on a property and criteria.
 * @param sourceArr - The array to search.
 * @param propName - The property name to match.
 * @param criteria - The value to match against.
 * @returns The index of the matching element or -1 if not found.
 */
export function getIndexByCriteria(
    sourceArr: Record<string | number, any>[],
    propName: string | number,
    criteria: any
) {
    for (let index = 0; index < sourceArr.length; index++) {
        const element = sourceArr[index];
        if (element[propName] == criteria) return index;
    }
    return -1;
}

/**
 * Builds parent-child relationships from a tree structure of nodes.
 * @param data - An array of TempNode objects.
 * @returns An object containing parent and child relationships.
 */
export function buildParentChildObjects(data: TempNode[]): {
    parents: { [key: number]: number[] };
    children: { [key: number]: number[] };
} {
    const parentObject: { [key: number]: number[] } = {};
    const childrenObject: { [key: number]: number[] } = {};

    function traverseNodes(node: TempNode, parentIDs: number[] = []) {
        if (!node || !node.CatId) {
            return;
        }

        const currentID = node.CatId;
        const combinedIDs = [...parentIDs, currentID];

        parentObject[currentID] = parentIDs;

        if (node.children && node.children.length > 0) {
            childrenObject[currentID] = node.children.map((child) => child.CatId);
            parentIDs.forEach(
                (parentId: number) => (childrenObject[parentId] = childrenObject[parentId].concat(childrenObject[currentID]))
            );
            node.children.forEach((child) => {
                traverseNodes(child, combinedIDs);
            });
        }
    }

    data.forEach((item) => {
        traverseNodes(item);
    });
    // Add default identity vars
    parentObject[-1] = [];
    return {
        parents: parentObject,
        children: childrenObject,
    };
}

/**
 * Formats a date string or timestamp into a readable date and time format.
 * @param dateString - The date string or timestamp to format.
 * @returns The formatted date and time string.
 */
export function formatDate(dateString: string | number | undefined) {
    if (dateString === undefined) {
        return "";
    }
    let dateValue = new Date();
    if (isNaN(Number(dateString))) {
        dateString = (dateString as string).replace(/-/g, "/");
        dateValue = new Date(dateString + " GMT");
    } else {
        dateValue = new Date(Number(dateString));
    }

    const dateOptions: object = {
        dateStyle: "long",
        timeZone: "GMT",
    };
    const timeoptions: object = {
        timeZone: "GMT",
        timeZoneName: "short",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    };
    return `${dateValue.toLocaleDateString("en-US", dateOptions)} ${dateValue.toLocaleTimeString("en-Uk", timeoptions)}`;
}

/**
 * Sanitizes a string value to prevent XSS attacks.
 * @param value - The string to sanitize.
 * @returns The sanitized string.
 */
export function sanitize_value(value: string) {
    const input = value == null ? "" : String(value);
    return convertHtmlEntityToAngleBrackets(DOMPurify.sanitize(input, { ALLOWED_TAGS: [] }));
}

/**
 * Converts HTML entities to angle brackets.
 * @param fieldText - The string to convert.
 * @returns The converted string.
 */
export const convertHtmlEntityToAngleBrackets = (fieldText: string) => {
    const htmlEntities = /&(lt|gt|amp);/;
    const isHtmlEntityUsed = htmlEntities.test(fieldText);
    if (isHtmlEntityUsed) {
        return fieldText.replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&");
    } else {
        return fieldText;
    }
};

/**
 * Calculates the UTF-8 size of a string.
 * @param input - The string to measure.
 * @returns The size of the string in bytes.
 */
export const get_utf8_size = function (input: string) {
    let size = 0;
    for (let i = 0; i < input.length; i++) {
        const codePoint = input.codePointAt(i);
        if (codePoint) {
            if (codePoint <= 0x7f) {
                size += 1;
            } else if (codePoint <= 0x7ff) {
                size += 2;
            } else if (codePoint <= 0xffff) {
                size += 3;
            } else if (codePoint <= 0x10ffff) {
                size += 4;
            }
        }
    }
    return size;
};

/**
 * Formats a percentage based on a numerator and denominator.
 * @param numerator - The numerator value.
 * @param denominator - The denominator value.
 * @param decimals - The number of decimal places to include.
 * @returns The formatted percentage string or undefined if invalid.
 */
export function percent(
    numerator: number | null | undefined,
    denominator: number | null | undefined,
    decimals: number
): string | undefined {
    if (
        numerator === undefined ||
        denominator === undefined ||
        numerator === null ||
        denominator === null ||
        denominator === 0
    ) {
        return undefined;
    }
    const val = (numerator * 100) / denominator;
    return val.toFixed(decimals) + " %";
}

const count_format = new Intl.NumberFormat("en-US");

/**
 * Formats a number with commas as thousands separators.
 * @param num - The number to format.
 * @returns The formatted number string or an empty string if null or undefined.
 */
export function format_count(num: number | null | undefined): string {
    if (num === null || num === undefined) return "";
    return count_format.format(num);
}

type FormatFunction = (a: number | null | undefined) => string;

/**
 * Returns a number formatter function with specified decimal places.
 * @param digits - The number of decimal places.
 * @returns A function that formats numbers to the specified decimal places.
 */
export function get_number_formater(digits: number): FormatFunction {
    const number_format = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
    return (num: number | null | undefined): string => {
        if (num === null || num === undefined) return "";
        return number_format.format(num);
    };
}

/**
 * Parses a value from session storage based on a key.
 * @param key - The key to retrieve the value from session storage.
 * @returns The parsed value or null if not found or invalid.
 */
export function parse_from_storage(storage: Storage, key: string): unknown | null {
    const str = storage.getItem(key);
    if (!str || str == "{}" || str == "[]") return null;
    try {
        return str && JSON.parse(str);
    } catch (err) {
        storage.removeItem(key);
        console.log("Error parsing from local storage " + key + " : " + str);
    }
}

/**
 * Converts new line characters in a string to paragraph tags.
 * @param input - The string to convert.
 * @returns The converted string with paragraph tags.
 */
export function nl2p(input: string): string {
    return input
        .split("\n")
        .map((line) => "<p>" + line + "</p>")
        .join("");
}

/**
 * Strips HTML tags from a string.
 * @param html - The string containing HTML.
 * @returns The string without HTML tags.
 */
export function stripHtmlTags(html: string): string {
    return DOMPurify.sanitize(html, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
}

/**
 * Prevents XSS by sanitizing the input HTML.
 * @param html - The HTML string to sanitize.
 * @param make_external_links - Whether to add target="_blank" and rel="noopener noreferrer" to external links.
 * @returns The sanitized string.
 */
export function preventXSS(html: NullableString, make_external_links = false): string {
    if (html === undefined || html === null) return "";

    if (make_external_links) {
        DOMPurify.addHook('afterSanitizeAttributes', function (node) {
            // Check if the current element is a link
            if ('href' in node) {
                node.setAttribute('target', '_blank');
                node.setAttribute('rel', 'noopener noreferrer');
            }
        });
    }
    return DOMPurify.sanitize(html, {
        USE_PROFILES: { html: true },
        ALLOWED_ATTR: ['target', 'href', 'rel', 'title', 'aria-label']
    });
}

/**
 * Add noopener noreferrer and target blank to external links in the input HTML to prevent security vulnerabilities.
 * @param html 
 * @returns 
 */
export function make_external_links(html: NullableString): string {
    if (html === undefined || html === null) return "";

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const links = doc.querySelectorAll('a');

    links.forEach(link => {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
    });

    return doc.body.innerHTML;
}

export function removeDuplicates(arr: any[], key: string | number) {
    const uniqueValues = new Set();
    return arr.filter((obj) => {
        const value = obj[key];
        if (!uniqueValues.has(value)) {
            uniqueValues.add(value);
            return true;
        }
        return false;
    });
}

/**
 * Flattens a nested array of Cat objects into a single array.
 * @param cats - The array of Cat objects to flatten.
 * @returns A flat array of Cat objects.
 */
export function CatsToFlatList(cats: Cat[]) {
    return cats?.map((c: Cat) => flatten_cat(c)).reduce((a: Cat[], c: Cat[]) => a.concat(c), []) ?? [];
}

/**
 * Recursively flattens a Cat object and its children.
 * @param cat - The Cat object to flatten.
 * @returns An array of flattened Cat objects.
 */
function flatten_cat(cat: Cat): Cat[] {
    if (!cat.children) {
        return [cat];
    }
    const child_cats = cat.children?.map((c) => flatten_cat(c)).reduce((a, c) => a.concat(c), []);
    return [cat].concat(child_cats);
}

/**
 * Prunes the selected categories from a list of Cat objects based on selected IDs.
 * @param cats - The array of Cat objects to prune.
 * @param selected_ids - The array of selected category IDs.
 * @returns The pruned array of Cat objects.
 */
export function pruneSelectedCats(cats: Cat[] | undefined, selected_ids: (number | string)[]) {
    if (!cats) {
        return [];
    }
    let new_cats = JSON.parse(JSON.stringify(cats)) as Cat[];
    new_cats = new_cats.filter((x) => selected_ids.includes(x.CatId));
    new_cats = new_cats.map((c) => {
        c.children = pruneSelectedCats(c.children ?? [], selected_ids);
        return c;
    });
    return new_cats;
}

/**
 * Formats a UTC time string into a human-readable date and time format.
 * @param utc_time_in_ymd_hmsts_format - The UTC time string to format.
 * @returns The formatted date and time string or undefined if invalid.
 */
export function date_time_format(utc_time_in_ymd_hmsts_format: NullableString) {
    if (utc_time_in_ymd_hmsts_format === undefined) {
        return undefined;
    }
    if (utc_time_in_ymd_hmsts_format === null) {
        return null;
    }
    try {
        const date = new Date(utc_time_in_ymd_hmsts_format);

        const options: Intl.DateTimeFormatOptions = {
            month: "long",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            timeZone: "UTC",
            hour12: false,
        };

        // Format the date part
        const datePart = date.toLocaleDateString("en-US", options);

        // Combine date and time with "UTC" timezone suffix
        return `${datePart} UTC`;
    } catch (error) {
        console.error("Date parse error with " + utc_time_in_ymd_hmsts_format, error);
        return `once`;
    }
}

/**
 * Creates a debounced version of a function that delays its execution.
 * @param func - The function to debounce.
 * @param timeout - The delay in milliseconds.
 * @returns A debounced version of the function.
 */
export function debounce<Params extends any[]>(
    func: (...args: Params) => any,
    timeout: number
): (...args: Params) => void {
    let timer: NodeJS.Timeout;
    return (...args: Params) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            func(...args);
        }, timeout);
    };
}

/**
 * Evaluates the strength of a password based on various criteria.
 * @param inValue - The password to evaluate.
 * @returns A string indicating the strength of the password.
 */
export function password_strength(inValue: string): string {
    const number = /([0-9])/,
        alphabets_lc = /([a-z])/,
        alphabets_uc = /([A-Z])/,
        special_characters = /([~!@#$%^&*\-_=+?><])/;

    if (inValue.length === 0) {
        return "";
    }
    if (inValue.length < 8) {
        return "weak";
    }
    if (
        inValue.match(number) &&
        inValue.match(alphabets_lc) &&
        inValue.match(alphabets_uc) &&
        inValue.match(special_characters)
    ) {
        return "strong";
    }
    return "medium";
}

const static_vars_keys: string[] = [
    'FirstName',
    'LastName',
    'Initials',
    'Gender',
    'BirthDate',
    'InclusionDate',
    'LastConsultation',
    'DeathDate',
    'Progress',
    'EmailId',
    'CustomId',
    'Age     ',
    'AgeAtInclusion',
    'FollowUp',
    'CountInSerie',
    'PatientId'
];

export function variableid2key(searched_id: number): string | null {
    if (searched_id > -1) return String(searched_id);
    const idx = -searched_id - 1;
    if (idx >= static_vars_keys.length) throw new Error(`Variable with id ${searched_id} does not exist`);
    return static_vars_keys[idx];
}

export function variablekey2id(varkey: string): number | null {
    const res = static_vars_keys.indexOf(varkey);
    if (res === -1) return null;
    return -res - 1;
}
